import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BriefcaseIcon,
  BellIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon,
  TruckIcon,
  ChevronDownIcon,
  ChevronDoubleLeftIcon,
  CubeIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ShieldCheckIcon,
  ServerIcon,
  LinkIcon,
  SignalIcon,
  CodeBracketIcon,
  KeyIcon,
  RectangleGroupIcon,
  UserPlusIcon,
  FlagIcon,
  BuildingOffice2Icon,
  InboxStackIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils';
import { useAuthStore } from '@/store';
import { useModuleMatrix } from '@/hooks/useEmployeeRights';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import type { RightsLevel, RightsModule } from '@/services/employeeRights';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import { homePathForRole, DEFAULT_HOME } from '@/routing/portalHome';
import { QuoteDeskOverdueBadge } from './QuoteDeskOverdueBadge';
import { BrokerRequestsPendingBadge } from './BrokerRequestsPendingBadge';

const QUOTE_DESK_PATH = '/insurance/quote-desk';
const BROKER_REQUESTS_PATH = '/insurance/broker-requests';

interface NavChild {
  name: string;
  href: string;
  roles?: string[];
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ElementType;
  children?: NavChild[];
  roles?: string[];
  // B4: the module/level the destination needs, on top of the role check
  // above. Resolved from the caller's matrix (`useModuleMatrix`), which is
  // all-true for admin roles and for roles outside the rights model -- so this
  // only ever NARROWS the role list.
  requires?: { module: RightsModule; level: RightsLevel };
  // #397: the insurer permission ("module.level") an insurer_employee's custom
  // role must hold. Every other role passes this check.
  insurerRequires?: string;
}

interface NavGroup {
  id: string;
  title: string | null;
  items: NavItem[];
  roles?: string[];
}

const navigationGroups: NavGroup[] = [
  // ── Dashboard (all roles) ──
  {
    id: 'main',
    title: null,
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ],
  },

  // ── SuperAdmin: Platform Management ──
  // Super Admin is scoped to Countries + Country Admins only (Task #188).
  // Items shared with broker/insurer/posp roles live in the Operations group
  // to avoid duplicate sidebar entries; only Platform-specific items remain
  // here (super_admin links, customer/posp shortcuts, quote shortcuts).
  {
    id: 'platform',
    title: null,
    items: [
      // P1: this was one entry labelled "My Contacts" for both roles, which
      // gave a POSP two contact-looking links -- this one and "Contacts"
      // (/contacts) below. /my-contacts shows the caller's OWN broker and
      // profile; it has never listed customers. A POSP clicking it saw no
      // customers and reported "Contacts does not show my customers", when the
      // customer list on /contacts was working correctly all along (verified:
      // 200 with count=1 for the live POSP account).
      //
      // Split by role so each label says what its page shows, leaving
      // "Contacts" as the single unambiguous customer list for a POSP.
      //
      // U-01: the customer's "My Contacts" entry is gone too. Its broker and
      // agent cards now sit on Settings > Profile ("Your advisors") and, per
      // policy, on the policy detail page; /my-contacts redirects a customer
      // there. A POSP's "My Broker" (/my-contacts) is untouched.
      { name: 'Countries', href: '/admin/geography/countries', icon: GlobeAltIcon, roles: ['super_admin'] },
      { name: 'Country Admins', href: '/admin/country-admins', icon: UsersIcon, roles: ['super_admin'] },
    ],
  },

  // ── CountryAdmin: Core Management ──
  {
    id: 'country-management',
    title: 'Management',
    roles: ['country_admin'],
    items: [
      {
        name: 'Brokers',
        icon: BuildingOfficeIcon,
        roles: ['country_admin'],
        children: [
          { name: 'All Brokers', href: '/brokers', roles: ['country_admin'] },
          // #389: compliance / KYC dossiers submitted from Broker Settings.
          { name: 'KYC Reviews', href: '/country/kyc-reviews', roles: ['country_admin'] },
        ],
      },
      {
        name: 'Insurance Companies',
        icon: BuildingLibraryIcon,
        roles: ['country_admin'],
        children: [
          { name: 'All Companies', href: '/country/insurance-companies', roles: ['country_admin'] },
          // DQ-346: insurer hospital lists, network scope and the doubtful-match queue.
          { name: 'Hospital Network', href: '/country/hospital-network', roles: ['country_admin'] },
        ],
      },
    ],
  },

  // ── CountryAdmin: Reports (#323) ──
  //
  // Analytics, Ledger Account Types and Fiscal Years had routes but no way in
  // from the sidebar. They live together under one Reports parent, with Audit
  // Logs (platform-level activity, scoped to the admin's country server-side).
  {
    id: 'country-reports',
    title: null,
    roles: ['country_admin'],
    items: [
      {
        name: 'Reports',
        icon: ChartBarIcon,
        roles: ['country_admin'],
        children: [
          { name: 'Analytics', href: '/country/analytics' },
          { name: 'Ledger Account Types', href: '/admin/ledger/account-types' },
          { name: 'Fiscal Years', href: '/admin/ledger/fiscal-years' },
          { name: 'Audit Logs', href: '/admin/audit-logs' },
          // #404: insurers' activity logs, scoped server-side to this country.
          { name: 'Insurer Activity', href: '/admin/insurer-activity' },
        ],
      },
    ],
  },

  // ── CountryAdmin: Master Data ──
  {
    id: 'master-data',
    title: 'Master Data',
    roles: ['country_admin'],
    items: [
      {
        name: 'Products',
        icon: CubeIcon,
        roles: ['country_admin'],
        children: [
          { name: 'Insurance Types', href: '/admin/products/types' },
          { name: 'Groups', href: '/admin/products/groups' },
          { name: 'Subgroups', href: '/admin/products/subgroups' },
          { name: 'Policy Types', href: '/admin/products/policy-types' },
          { name: 'Products', href: '/admin/products' },
        ],
      },
      {
        name: 'Vehicles',
        icon: TruckIcon,
        roles: ['country_admin'],
        children: [
          { name: 'Makes', href: '/admin/vehicles/makes' },
          { name: 'Models', href: '/admin/vehicles/models' },
          { name: 'Variants', href: '/admin/vehicles/variants' },
          { name: 'Fuel Types', href: '/admin/vehicles/fuel-types' },
          { name: 'Body Types', href: '/admin/vehicles/body-types' },
        ],
      },
      {
        name: 'Geography',
        icon: GlobeAltIcon,
        roles: ['country_admin'],
        children: [
          // Country admins must not manage Countries (super_admin scope only).
          { name: 'Countries', href: '/admin/geography/countries', roles: ['super_admin'] },
          { name: 'States', href: '/admin/geography/states' },
          { name: 'Cities', href: '/admin/geography/cities' },
          { name: 'Pincodes', href: '/admin/geography/pincodes' },
          { name: 'RTOs', href: '/admin/geography/rtos' },
        ],
      },
    ],
  },

  // ── CountryAdmin: Setup ──
  //
  // WITHDRAWN. All three setup wizards -- Onboarding, Product Setup and User
  // Setup -- are mockups: each `onComplete` waits a second on a setTimeout,
  // toasts "created successfully!" and shows a success screen without calling
  // any API. Onboarding's countryService/insurerService/brokerService calls are
  // present as commented-out lines; Product Setup's dropdowns are hardcoded
  // fixtures under a "Mock data - in production, these would come from API"
  // comment.
  //
  // They were reachable by a country admin, so the platform has been telling
  // its administrators that countries, insurers, brokers, products and users
  // were created when nothing was. Removing the way in is the smallest change
  // that stops that; wiring them to the real endpoints is separate work, and
  // every entity they cover already has a working Add form elsewhere.
  //
  // The routes are withdrawn too (App.tsx) -- leaving them reachable by URL
  // would keep the same lie one bookmark away.

  // ── Operations (broker/insurer/posp roles) ──
  {
    id: 'operations',
    title: null,
    items: [
      { name: 'Contacts', href: '/contacts', icon: UsersIcon, roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp'], requires: { module: 'contacts', level: 'read' } },
      { name: 'Policies', href: '/policies', icon: DocumentTextIcon, roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'insurer_admin', 'insurer_employee', 'customer'], requires: { module: 'policies', level: 'read' }, insurerRequires: 'policies.read' },
      // C1: straight to the list, not to a hub page whose three cards were
      // "All Quotes" plus two actions QuotesListPage already offers as buttons.
      //
      // #352 (owner decision): no Quotes for insurer roles. There is no insurer
      // underwriting / quote-approval workflow, and the screen behind it is the
      // broker's quote desk (New Quote, Batch Quotes).
      { name: 'Quotes', href: '/quotes', icon: ClipboardDocumentListIcon, roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp'], requires: { module: 'quotes', level: 'read' } },
      // Item 7b: the customer's own Quotes section. A SEPARATE entry pointing
      // at /my-quotes, not 'customer' added to the line above -- /quotes is
      // QuotesListPage, which is broker-side (New Quote, Batch Quotes, rows
      // that open the broker detail screen). The roles on the two entries are
      // disjoint, so exactly one of them ever renders.
      { name: 'My Quotes', href: '/my-quotes', icon: ClipboardDocumentListIcon, roles: ['customer'] },
      // `posp`/`super_posp` join this list with the CLAIMS lane's backend
      // change: an agent may now raise a claim for a policy they sold. The
      // matching route gates in App.tsx were widened in the same batch -- a nav
      // entry whose route still refused the role would be the exact "Access
      // Denied bounce" C1 is about.
      { name: 'Claims', href: '/claims', icon: BriefcaseIcon, roles: ['broker_admin', 'broker_employee', 'insurer_admin', 'insurer_employee', 'customer', 'posp', 'super_posp'], requires: { module: 'claims', level: 'read' }, insurerRequires: 'claims.read' },
      // #393: the customer's grievances (ticket, TAT, escalation matrix).
      { name: 'My Grievances', href: '/my-grievances', icon: FlagIcon, roles: ['customer'] },
      // DQ-346: find a cashless network hospital for the customer's health insurer.
      { name: 'Cashless Hospitals', href: '/cashless-hospitals', icon: BuildingOffice2Icon, roles: ['customer'] },
      // #273: Leads gets its own entry again -- it is a distinct pipeline with
      // its own statuses, its own form and a bulk "Convert to Customer", none
      // of which the Contacts list shows.
      //
      // Leads Read gates this for EMPLOYEES (BASELINE gives them none; the old
      // `view_leads` grant carries it); a broker admin auto-passes every right,
      // so the section never disappears for the owner.
      { name: 'Leads', href: '/leads', icon: UserPlusIcon, roles: ['broker_admin', 'broker_employee'], requires: { module: 'leads', level: 'read' } },
      // P1 (15 Sep): the POSP's own entry -- the leads ASSIGNED to them.
      //
      // A SEPARATE entry with disjoint roles. It carries the same leads Read
      // requirement, which BASELINE grants every POSP (the server never asked
      // a POSP for `view_leads`), so it only disappears when a broker has
      // explicitly denied that POSP leads.
      { name: 'Leads', href: '/leads', icon: UserPlusIcon, roles: ['posp', 'super_posp'], requires: { module: 'leads', level: 'read' } },
      // R1 (20 Sep): posp/super_posp share the broker entry rather than getting
      // a separate one (unlike Leads above, which points a POSP at a different
      // scope of the same page): the renewals page IS the same page, narrowed
      // server-side to the policies the agent sold. The renewals Read
      // requirement rides along -- BASELINE grants it to every POSP, so the
      // entry appears without anybody switching it on, and vanishes only where
      // a broker has explicitly denied that agent renewals.
      { name: 'Renewals', href: '/renewals', icon: ArrowPathIcon, roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'customer'], requires: { module: 'renewals', level: 'read' } },
      // The broker's OWN vehicles, keyed by registration number.
      //
      // This used to be five master lists -- Makes, Models, Variants, Fuel
      // Types, Body Types -- which describe the CATALOGUE, not vehicles: a
      // broker looking for "MH01AB1234" could not find it anywhere under a
      // menu called Vehicle Database. Those masters are still routable and
      // still in the Country Admin's own Master Data > Vehicles menu, and the
      // quote and policy forms still pick from them; they are simply no longer
      // what this entry means.
      {
        name: 'Vehicle Database',
        href: '/vehicle-database',
        icon: TruckIcon,
        roles: ['broker_admin', 'broker_employee'],
      },
      // CUSTOMER-3: servicing requests get their own section instead of living
      // under Renewals. CUSTOMER-5 puts the same screen in front of the broker
      // side and the assigned POSP -- the queryset is what scopes each of them.
      {
        name: 'Servicing Requests',
        href: '/servicing-requests',
        icon: WrenchScrewdriverIcon,
        roles: ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'customer'],
        requires: { module: 'servicing', level: 'read' },
      },
      // Queue #124: these four shortcuts used to be a card on the customer's
      // dashboard front page. They are a navigation menu, not a summary, so
      // they belong here.
      //
      // Deliberately a SEPARATE entry rather than children on Servicing
      // Requests above: an item with `children` renders as a Disclosure
      // toggle instead of a link, and one whose children all filter out by
      // role returns null -- so nesting them would have turned Servicing
      // Requests into a toggle for customers and removed it outright for
      // every broker-side role.
      // Change Request: WITHDRAWN, folded into Servicing Requests.
      //
      // It was a second door onto the same screen, and three of its four links
      // were dead: they filtered on `vehicle_change`, `mobile_change` and
      // `email_change`, none of which are ServiceRequest types. The real ones
      // are `vehicle_transfer` and `contact_update`, and a customer clicking
      // "Mobile Change" got an unfiltered or empty list either way.
      //
      // Every change a customer can request is now a request TYPE on the one
      // Servicing Requests screen, which is where the rest of the workflow
      // (documents, SLA, status) already lived.
      // #364 (owner decision): Commissions is the BROKER's commission desk
      // (calculations, ledger, POSP payouts), so insurer_admin is no longer
      // offered it.
      { name: 'Commissions', href: '/commissions', icon: BanknotesIcon, roles: ['broker_admin'], requires: { module: 'commissions', level: 'read' } },
      // #688: the bank's own statements (upload, lines, matching).
      { name: 'Reconciliation', href: '/reconciliation/bank-statements', icon: BanknotesIcon, roles: ['broker_admin', 'broker_employee'], requires: { module: 'reconciliation', level: 'read' } },
      { name: 'POSP ledger', href: '/commissions/posp-ledger', icon: BanknotesIcon, roles: ['broker_admin', 'broker_employee'], requires: { module: 'commissions', level: 'read' } },
      { name: 'My ledger', href: '/portal/ledger', icon: BanknotesIcon, roles: ['posp', 'super_posp'] },
      // P2: "My Portal" is now "Reports", because that is what is left on it
      // once the profile half moves to Settings -- stats, the policies table,
      // payouts, targets and quick actions.
      //
      // The URL stays /portal: the label is what was reported, and moving the
      // path would break every existing bookmark and the /portal/:id detail
      // route under it for nothing.
      //
      // NOTE the entry immediately below is ALSO called "Reports" and points at
      // /reports. The two role lists are disjoint -- posp/super_posp here,
      // broker/insurer roles there -- so exactly one ever renders and no user
      // sees two identically-named links. They are different screens.
      { name: 'Reports', href: '/portal', icon: ChartBarIcon, roles: ['posp', 'super_posp'] },
      {
        name: 'Reports',
        href: '/reports',
        icon: ChartBarIcon,
        // #364 (owner decision): no insurer_admin. Every tab is a broker report
        // (Commission, Statutory Returns, Renewals, Customers, ...) and the API
        // scopes an insurer to nothing.
        roles: ['broker_admin', 'broker_employee'],
        requires: { module: 'reports', level: 'read' },
      },
    ],
  },

  // ── User Management: WITHDRAWN ──
  //
  // A Country Admin manages BROKERS, not the people inside them. This section
  // held exactly one link (Users), whose list for a country admin was already
  // just the brokers plus insurer staff -- so it was a second, weaker way into
  // the same records, and it invited a country admin to reach past a broker
  // into that broker's own employees and POSPs, which are the Broker Admin's
  // to run.
  //
  // Everything it offered still exists, closer to the thing it acts on:
  //   * brokers  -> All Brokers, which now carries Edit / Suspend / Block
  //                 directly on the row (`BrokersListPage`).
  //   * insurer staff -> Master Data > Insurers > Employees, unchanged.
  //   * a broker's own staff -> the Broker Admin's own Channels > POSPs and
  //                 Employees screens, which is where they belonged.
  //
  // The routes drop `country_admin` too (App.tsx) -- leaving them reachable by
  // URL would keep the same second door one bookmark away.

  // ── Insurance Admin ──
  {
    id: 'insurance-admin',
    title: 'Insurance Admin',
    roles: ['insurer_admin', 'insurer_employee'],
    items: [
      { name: 'Products', href: '/insurance/policies', icon: DocumentTextIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'products.read' },
      // #355: the brokers connected to this insurer.
      { name: 'Brokers', href: '/insurance/brokers', icon: BuildingOffice2Icon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'brokers.read' },
      { name: 'Channels', href: '/insurance/channels', icon: SignalIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'channels.read' },
      // #356: brokers asking to sell this insurer's products.
      { name: 'Broker Requests', href: '/insurance/broker-requests', icon: UserPlusIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'broker_requests.read' },
      // #362: the insurer's own business report (not the broker Reports module).
      { name: 'Business Reports', href: '/insurance/reports', icon: ChartBarIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'policies.read' },
      // #399: quote requests brokers sent this insurer to price.
      { name: 'Quote Desk', href: '/insurance/quote-desk', icon: InboxStackIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'quote_inbox.read' },
      // #403: connection health, call log, go-live requests and webhooks.
      { name: 'API Console', href: '/insurance/api-console', icon: CommandLineIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'api_console.read' },
      // #410: earned / paid / payable commission on the insurer's policies.
      { name: 'Commission', href: '/insurance/commissions', icon: BanknotesIcon, roles: ['insurer_admin', 'insurer_employee'], insurerRequires: 'commission_schedules.read' },
      // 24 Sep: the insurer admin adds their own Managers and Staff here.
      { name: 'Team', href: '/insurance/team', icon: UsersIcon, roles: ['insurer_admin'] },
      // #397: the insurer admin's custom roles, assigned from Team.
      { name: 'Roles', href: '/insurance/roles', icon: KeyIcon, roles: ['insurer_admin'] },
      // DQ-346: the insurer's own cashless hospital list (upload with preview).
      { name: 'Hospital Network', href: '/insurance/hospital-network', icon: BuildingOffice2Icon, roles: ['insurer_admin'] },
      // #404: the insurer's append-only activity log. An employee sees only
      // their own activity there (the API scopes it), so it is labelled so.
      { name: 'Activity Log', href: '/insurer/activity-log', icon: ClipboardDocumentListIcon, roles: ['insurer_admin'] },
      { name: 'My Activity', href: '/insurer/activity-log', icon: ClipboardDocumentListIcon, roles: ['insurer_employee'] },
    ],
  },

  // ── Broker Admin ──
  {
    id: 'broker-admin',
    title: null,
    roles: ['broker_admin', 'broker_employee'],
    items: [
      // Policy Access now lives per-company under the "Insurance Companies" detail page (Policies tab).
      { name: 'Insurance Companies', href: '/broker/insurers', icon: BuildingLibraryIcon, roles: ['broker_admin'] },
      { name: 'API Credentials', href: '/broker/credentials', icon: KeyIcon, roles: ['broker_admin'] },
      { name: 'Webhooks', href: '/broker/webhooks', icon: SignalIcon, roles: ['broker_admin'] },
      // B4 decision D2: Channels stays broker-admin only. The route admitted
      // broker_employee while `BrokerChannelViewSet` is `IsBrokerAdmin`, so an
      // employee clicking this landed on a screen of 403s.
      { name: 'Channels', href: '/broker/channels', icon: RectangleGroupIcon, roles: ['broker_admin'], requires: { module: 'channels', level: 'read' } },
      { name: 'Public Site', href: '/broker/microsite', icon: GlobeAltIcon, roles: ['broker_admin'] },
      { name: 'Rights Management', href: '/broker/rights', icon: ShieldCheckIcon, roles: ['broker_admin'] },
      // 9a: a page with no link is a page nobody finds -- the reason the
      // previous Audit Logs screen sat unreachable while calling a 404 route.
      { name: 'Audit Log', href: '/admin/audit-logs', icon: ClipboardDocumentCheckIcon, roles: ['broker_admin'] },
      // #263: premium targets per broker and per POSP, set and tracked monthly.
      { name: 'Targets', href: '/broker/targets', icon: FlagIcon, roles: ['broker_admin'] },
    ],
  },

  // ── Country Admin Configuration ──
  {
    id: 'country-config',
    title: 'Configuration',
    roles: ['country_admin'],
    items: [
      { name: 'API Products', href: '/country/api-products', icon: ServerIcon, roles: ['country_admin'] },
      { name: 'Eligibility Rules', href: '/admin/eligibility-rules', icon: ShieldCheckIcon, roles: ['country_admin'] },
      { name: 'Master Mappings', href: '/admin/master-mappings', icon: ServerIcon, roles: ['country_admin'] },
      { name: 'Integrations', href: '/admin/integrations', icon: LinkIcon, roles: ['country_admin'] },
      { name: 'Integration Status', href: '/admin/insurers/integration-status', icon: ChartBarIcon, roles: ['country_admin'] },
      { name: 'Microsite Base', href: '/admin/microsite-base', icon: GlobeAltIcon, roles: ['country_admin'] },
      { name: 'Messaging Channels', href: '/country/messaging-channels', icon: ChatBubbleLeftRightIcon, roles: ['country_admin'] },
    ],
  },
  {
    id: 'admin',
    title: null,
    items: [
      { name: 'Dev Log', href: '/admin/devlog', icon: CodeBracketIcon, roles: ['super_admin'] },
      { name: 'Communications', href: '/communications', icon: ChatBubbleLeftRightIcon, roles: ['super_admin', 'broker_admin', 'broker_employee'] },
      // #399: insurer employees get desk notifications too.
      { name: 'Notifications', href: '/notifications', icon: BellIcon, roles: ['super_admin', 'broker_admin', 'broker_employee', 'insurer_admin', 'insurer_employee'] },
      { name: 'Settings', href: '/hub/settings', icon: Cog6ToothIcon, roles: ['super_admin', 'country_admin', 'broker_admin'] },
      // C1/P1: the portal roles get Settings in the nav too, pointing STRAIGHT
      // at /settings rather than at the hub above. The hub lists the admin
      // configuration screens (branding, gateways, domain, support categories),
      // none of which a customer or an agent may open -- it would render
      // "Nothing available here for your role." Role-disjoint from the entry
      // above, so no user ever sees two Settings links.
      {
        name: 'Settings',
        href: '/settings',
        icon: Cog6ToothIcon,
        roles: ['customer', 'posp', 'super_posp'],
      },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Desktop icon-rail mode. Ignored below `lg`, where the sidebar is a drawer. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Called when a collapsed item needs the full labels back (parent items). */
  onExpand?: () => void;
}

/**
 * A child like "Products" (/admin/products) is a path prefix of its siblings
 * (/admin/products/types), so NavLink marks both active. Only the most specific
 * matching sibling should be highlighted; a prefix child still stays active on
 * routes no sibling claims (e.g. /admin/products/:id).
 */
function hasMoreSpecificActiveSibling(
  href: string,
  siblings: { href?: string }[],
  pathname: string
): boolean {
  return siblings.some(
    (s) =>
      !!s.href &&
      s.href !== href &&
      s.href.startsWith(`${href}/`) &&
      (pathname === s.href || pathname.startsWith(`${s.href}/`))
  );
}

function NavItemLink({
  item,
  onClose,
  userRole,
  iconOnly = false,
  onExpand,
}: {
  item: NavItem;
  onClose: () => void;
  userRole: string;
  iconOnly?: boolean;
  onExpand?: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { pathname } = useLocation();

  if (item.children) {
    const visibleChildren = item.children.filter(
      (child) => !child.roles || child.roles.includes(userRole)
    );

    if (visibleChildren.length === 0) return null;

    // On the icon rail a submenu has nowhere to open, so the icon expands the
    // sidebar instead. Every child stays reachable -- one extra click, no
    // navigation item removed.
    if (iconOnly) {
      return (
        <button
          type="button"
          onClick={onExpand}
          title={item.name}
          aria-label={`${item.name} (expand sidebar)`}
          className={cn(
            'flex items-center justify-center w-full px-3 py-2.5 rounded-lg transition-colors',
            'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
            'dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
          )}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        </button>
      );
    }

    return (
      <Disclosure as="div" defaultOpen={isExpanded}>
        {({ open }) => (
          <>
            <Disclosure.Button
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                'dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
              )}
            >
              <span className="flex items-center">
                <item.icon className="w-5 h-5 mr-3 flex-shrink-0" />
                {item.name}
              </span>
              <ChevronDownIcon
                className={cn(
                  'w-4 h-4 transition-transform duration-200',
                  open && 'rotate-180'
                )}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="pl-10 space-y-1 mt-1">
              {visibleChildren.map((child) => (
                <NavLink
                  key={child.href}
                  to={child.href}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2 text-sm rounded-lg transition-colors',
                      isActive && !hasMoreSpecificActiveSibling(child.href, visibleChildren, pathname)
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                    )
                  }
                >
                  {child.name}
                </NavLink>
              ))}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={onClose}
      // Collapsed, the icon is the only visible content -- the label has to
      // reach assistive tech (aria-label) and the mouse (title) some other way.
      title={iconOnly ? item.name : undefined}
      aria-label={iconOnly ? item.name : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
          iconOnly && 'justify-center',
          isActive
            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
        )
      }
    >
      <item.icon
        className={cn('w-5 h-5 flex-shrink-0', !iconOnly && 'mr-3')}
        aria-hidden="true"
      />
      {!iconOnly && item.name}
      {!iconOnly && item.href === QUOTE_DESK_PATH && <QuoteDeskOverdueBadge />}
      {!iconOnly && item.href === BROKER_REQUESTS_PATH && <BrokerRequestsPendingBadge />}
    </NavLink>
  );
}

export function Sidebar({
  isOpen,
  onClose,
  collapsed = false,
  onToggleCollapse,
  onExpand,
}: SidebarProps) {
  const { user } = useAuthStore();
  const userRole = user?.role || '';
  // Collapse is a DESKTOP affordance. Below `lg` the sidebar is an off-canvas
  // drawer that only appears when the user asked for it, and an icon rail there
  // would be a worse version of the thing they just opened -- so the drawer
  // always shows full labels regardless of the stored preference.
  const isDesktop = useIsDesktop();
  const iconOnly = collapsed && isDesktop;
  // B4 self-gating: one matrix lookup per item, so any module/level declared
  // on an item is honoured (the old per-code map silently ignored codes it had
  // not been taught). While a gated user's first `me` answer is loading, or if
  // it failed, `matrix` is undefined and every `requires` item stays hidden --
  // fail closed, as the API is. A FAILED request says so above the menu, so a
  // shorter menu is never mistaken for revoked access.
  const { matrix, isError: rightsError, retry: retryRights } = useModuleMatrix();
  const { can: canInsurer } = useInsurerPermissions();

  const filteredGroups = navigationGroups
    .filter((group) => {
      if (!group.roles) return true;
      return group.roles.includes(userRole);
    })
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => {
          if (item.roles && !item.roles.includes(userRole)) return false;
          if (item.requires && !matrix?.[item.requires.module]?.[item.requires.level]) return false;
          if (item.insurerRequires && !canInsurer(item.insurerRequires)) return false;
          return true;
        })
        // #350: "Dashboard" is each role's home. For an insurer that is
        // /insurer/dashboard, not the broker dashboard at /dashboard.
        .map((item) => (item.href === DEFAULT_HOME ? { ...item, href: homePathForRole(userRole) } : item)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600/75 dark:bg-gray-900/80 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        aria-label="Main navigation"
        data-collapsed={iconOnly ? 'true' : 'false'}
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-[transform,width] duration-300 ease-in-out lg:translate-x-0',
          // The narrow rail is `lg`-only: the mobile drawer keeps its full
          // width so its labels have somewhere to sit.
          collapsed && 'lg:w-16',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header: brand first in DOM order, then the collapse toggle
              right after it. */}
          <div
            className={cn(
              'flex items-center h-16 border-b border-gray-200 dark:border-gray-700',
              // At `lg`, px-4 + the button's own p-2 puts the toggle icon on
              // the same 24px left edge as every nav icon below it. Below `lg`
              // the toggle is hidden, so the brand keeps its original px-6.
              iconOnly ? 'px-2 justify-center' : 'px-6 lg:px-4 gap-2'
            )}
          >
            {/* The 4rem rail has room for one control, and the toggle is the
                only way back to the full sidebar -- so the brand yields to it
                there rather than crowding beside it. */}
            {!iconOnly && (
              <Link
                to={homePathForRole(userRole)}
                aria-label="base.Policy home"
                data-testid="sidebar-brand-link"
                onClick={onClose}
                className="flex items-center gap-2 min-w-0"
              >
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">bp</span>
                </div>
                <span className="text-xl font-bold text-gray-900 dark:text-white truncate">
                  base<span className="text-primary-600 dark:text-primary-400">.Policy</span>
                </span>
              </Link>
            )}

            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                // Icon-only in BOTH states, so these three attributes are the
                // whole label: aria-label for assistive tech, title for the
                // mouse, aria-pressed for the state. Dropping any of them
                // leaves the control anonymous.
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-pressed={collapsed}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={cn(
                  // `lg`-only: below it the sidebar is a drawer dismissed with
                  // the backdrop, and a rail toggle there would do nothing.
                  'hidden lg:flex items-center justify-center flex-shrink-0 p-2 rounded-lg transition-colors',
                  'text-gray-500 hover:bg-gray-100 hover:text-gray-900',
                  'dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                )}
              >
                <ChevronDoubleLeftIcon
                  className={cn(
                    'w-5 h-5 flex-shrink-0 transition-transform duration-200',
                    collapsed && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {rightsError && (
              <div
                role="alert"
                className={cn(
                  'mb-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800',
                  'dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
                  iconOnly ? 'p-1 text-center' : 'px-3 py-2 text-xs'
                )}
              >
                {!iconOnly && (
                  <p>Could not load your access. Some menu items are hidden until it loads.</p>
                )}
                <button
                  type="button"
                  onClick={retryRights}
                  aria-label={iconOnly ? 'Could not load your access. Try again' : undefined}
                  title={iconOnly ? 'Could not load your access. Try again' : undefined}
                  className={cn('font-semibold underline', !iconOnly && 'mt-1')}
                >
                  {iconOnly ? '!' : 'Try again'}
                </button>
              </div>
            )}
            {filteredGroups.map((group) => (
              <div key={group.id} className={group.title ? 'pt-4 first:pt-0' : ''}>
                {group.title &&
                  (iconOnly ? (
                    // The heading has no room on the rail; a rule keeps the
                    // grouping legible instead of running every section together.
                    <div
                      role="separator"
                      aria-label={group.title}
                      className="mx-2 mb-2 border-t border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <p className="px-3 mb-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      {group.title}
                    </p>
                  ))}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavItemLink
                      // Name alone is no longer unique: P2 gave the POSP's
                      // /portal entry the label "Reports", which the broker
                      // /reports entry already had. Their roles are disjoint so
                      // only one ever RENDERS, but both are mapped over here,
                      // so the key has to carry the destination too.
                      key={`${item.name}:${item.href ?? 'group'}`}
                      item={item}
                      onClose={onClose}
                      userRole={userRole}
                      iconOnly={iconOnly}
                      onExpand={onExpand}
                    />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* C2/P4: the "Insurance Broker Platform / v1.0.0" strip is gone.
              It sat here in the footer, OUTSIDE the role-filtered <nav> and
              guarded only by {!iconOnly}, so it rendered for every role with an
              expanded sidebar -- not just the customer and POSP portals the
              request names. Removing it removes it everywhere, which is the
              agreed scope. The version number was hard-coded and had not
              tracked a release since it was typed, so nothing reads it.

              The footer element goes with it rather than leaving an empty
              bordered sliver above the fold. */}
        </div>
      </aside>
    </>
  );
}
